<template>
  <section>
    <div class="w-full max-w-5xl mx-auto sm:px-4 card">
      <div class="grid grid-cols-12 pt-6 pb-4 bg-white border border-black sm:px-6 md:gap-x-8 lg:pt-12 lg:px-12 lg:pb-6 gap-y-6">
        <!-- Left Section -->
        <div class="col-span-12 lg:col-span-5 xs-max:px-4">
          <h2 class="text-xl font-bold font-ww-bold text-center lg:text-left text-[#3B3B3B] sm:text-xl md:text-2xl xl:text-28" v-html="title"></h2>
          <p class="lg:text-left text-center text-[#3B3B3B] mt-2 md:text-lg text-base" v-html="subtitle"></p>

          <div class="grid grid-cols-1 gap-y-8">
            <div class="mt-8 space-y-2">
              <label for="cupsPerWeek" class="block text-base font-ww-light">{{ cupsLabel }}</label>
              <input id="cupsPerWeek" type="number" min="1" max="50" v-model="cupsPerWeek" class="w-full p-2 border rounded-md" />
            </div>

            <div class="space-y-2">
              <label for="currentPrice" class="block text-base font-ww-light">{{ priceLabel }}</label>
              <input id="currentPrice" type="number" min="1" step="0.50" v-model="currentPrice" class="w-full p-2 border rounded-md" />
            </div>

            <div class="space-y-2">
              <label for="monthsToProject" class="block text-base font-ww-light">{{ monthsLabel }}</label>
              <input id="monthsToProject" type="number" min="1" max="24" v-model="monthsToProject" class="w-full p-2 border rounded-md" />
            </div>
          </div>
        </div>

        <!-- Right Section -->
        <div class="col-span-12 lg:col-span-7">
          <div class="p-4 rounded-md bg-[#F0FDF4] mb-2 xs-max:mx-4">
            <h3 class="text-lg font-bold text-center text-green-700 md:text-xl font-ww-bold">
              {{ projectedSavingsLabel }}: ${{ totalSavings }}
            </h3>
            <p class="mt-1 text-xs md:text-sm text-center text-[#3B3B3B]">{{ savingsDescription }}</p>
          </div>

          <ClientOnly>
            <apexchart type="line" height="400" :options="chartOptions" :series="chartSeries" />
          </ClientOnly>

          <div class="gap-4 text-sm">
          <div class="flex items-center justify-end space-x-2">
            <Icon name="mdi:coffee" class="text-blue-500" />
            <span class="text-xs font-ww-light">Free 8th order included</span>
          </div>
          <!-- <div class="flex items-center justify-center space-x-2">
            <Icon name="mdi:trending-up" class="text-red-500" />
            <span class="text-xs font-ww-light">15% projected annual inflation</span>
          </div>
          <div class="flex items-center justify-center space-x-2">
            <Icon name="mdi:currency-usd" class="text-green-500" />
            <span class="text-xs font-ww-light">Price locked for 12 months</span>
          </div> -->
        </div>
        </div>
      </div>
    </div>
  </section>
</template>


<script setup>
import { ref, computed, onMounted } from 'vue'

// Props for dynamic content
const props = defineProps({
  title: {
    type: String,
    default: 'Coffee Cost Calculator',
  },
  subtitle: {
    type: String,
    default: 'See how much you\'ll save with our Anti-Inflation Shield + Rewards',
  },
  cupsLabel: {
    type: String,
    default: 'Cups per week',
  },
  priceLabel: {
    type: String,
    default: 'Current price per cup',
  },
  monthsLabel: {
    type: String,
    default: 'Months to project',
  },
  projectedSavingsLabel: {
    type: String,
    default: 'Your Projected Savings',
  },
  savingsDescription: {
    type: String,
    default: 'Based on current industry price increase projections + rewards',
  },
})

// State
const cupsPerWeek = ref(5)
const currentPrice = ref(4.50)
const monthsToProject = ref(12)
const isClient = ref(false)

// Constants
const INFLATION_RATE = 0.15 // 15% annual inflation

onMounted(() => {
  isClient.value = true
})

// Calculations
const calculateSavings = computed(() => {
  if (!isClient.value) return []

  const data = []
  const monthlyInflationRate = INFLATION_RATE / 12
  let regularTotal = 0
  let shieldedTotal = 0
  let monthlyPurchases = cupsPerWeek.value * 4

  for (let month = 0; month <= monthsToProject.value; month++) {
    const inflatedPrice = currentPrice.value * (1 + monthlyInflationRate * month)
    const shieldPrice = currentPrice.value

    const regularCost = inflatedPrice * monthlyPurchases
    const shieldCost = shieldPrice * monthlyPurchases

    const shieldDiscountedCost = shieldCost * 0.875

    regularTotal += regularCost
    shieldedTotal += shieldDiscountedCost

    data.push({
      month: `Month ${month}`,
      regular: parseFloat(regularTotal.toFixed(2)),
      shield: parseFloat(shieldedTotal.toFixed(2)),
    })
  }

  return data
})

const totalSavings = computed(() => {
  if (!isClient.value) return '0.00'
  const data = calculateSavings.value
  const lastMonth = data[data.length - 1]
  return (lastMonth.regular - lastMonth.shield).toFixed(2)
})

// Chart configuration
const chartOptions = computed(() => ({
  chart: {
    type: 'line',
    zoom: { enabled: false },
    toolbar: {
      show: true,
      tools: {
        download: false
      }
    }
  },
  stroke: {
    curve: 'smooth',
    width: 2,
  },
  xaxis: {
    categories: calculateSavings.value.map((item) => item.month),
  },
  colors: ['#ef4444', '#22c55e'],
  legend: {
    position: 'bottom',
  },
  grid: {
    borderColor: '#f3f4f6',
    row: {
      colors: ['#fff', '#f9fafb'],
    },
  },
}))

const chartSeries = computed(() => {
  if (!isClient.value) return []
  return [
    {
      name: 'Without Shield',
      data: calculateSavings.value.map((item) => item.regular),
    },
    {
      name: 'With Shield + Rewards',
      data: calculateSavings.value.map((item) => item.shield),
    },
  ]
})

// Builder.io configuration
defineBuilderComponent({
  friendlyName: 'ChartSavingCalculator',
  inputs: [
    {
      name: 'title',
      type: 'richText',
      friendlyName: 'Section Title',
      defaultValue: 'Coffee Cost Calculator',
    },
    {
      name: 'subtitle',
      type: 'richText',
      friendlyName: 'Section Subtitle',
      defaultValue: 'See how much you\'ll save with our Anti-Inflation Shield + Rewards',
    },
    {
      name: 'cupsLabel',
      type: 'text',
      friendlyName: 'Cups Label',
      defaultValue: 'Cups per week',
    },
    {
      name: 'priceLabel',
      type: 'text',
      friendlyName: 'Price Label',
      defaultValue: 'Current price per cup',
    },
    {
      name: 'monthsLabel',
      type: 'text',
      friendlyName: 'Months Label',
      defaultValue: 'Months to project',
    },
    {
      name: 'projectedSavingsLabel',
      type: 'text',
      friendlyName: 'Projected Savings Label',
      defaultValue: 'Your Projected Savings',
    },
    {
      name: 'savingsDescription',
      type: 'text',
      friendlyName: 'Savings Description',
      defaultValue: 'Based on current industry price increase projections + rewards',
    },
  ],
})
</script>
